'use client';

import { useState } from 'react';

import { trackShippingAddressEvents } from '@libs/analytics';
import { removeAddress, updateAddress, updateShippingAddress, withPodsAddressId } from '@libs/api-proxy-client';
import {
    AccountAddressesSynced,
    FinalError,
    logSentryError,
    SentryAcpAutoshipErrors as SentryErrors,
    SubscriptionOrder,
    useUserSession,
} from '@libs/common-next';
import { getCountry } from '@libs/core';
import { PROFILE_DATA, SERVER_ERROR_PRIMARY_TEXT, SnackbarUtils } from '@libs/shared-ui';

export type DrawerStateOptions =
    | 'AddressList'
    | 'AddAddress'
    | 'EditAddress'
    | 'RemoveAddress'
    | 'VerifyCvv'
    | 'AddressUpdated';

export interface HandleDrawerNavigation {
    close: () => void;
    back: () => void;
    navigate: (state: DrawerStateOptions, clearStack?: boolean) => void;
}

export interface UseRemoveAddressProps {
    defaultAddress: AccountAddressesSynced | undefined;
    addresses: AccountAddressesSynced[] | undefined;
    order: SubscriptionOrder | undefined;
    addressToRemove: AccountAddressesSynced | undefined;
    handleDrawerNavigation: HandleDrawerNavigation;
    hasSuccessHandler?: boolean;
    isFraudValidationNeeded?: boolean;
    siteSection?: string;
    onUpdate: (options?: { ignoreLoadingState: boolean }) => Promise<void>;
    handleBack: () => void;
    /**
     * Sentry error reference used to log a remove-address failure.
     * Allows callers (e.g. Autoship page) to override the default and report a more specific error.
     */
    sentryErrorOnRemoveFailed?: FinalError;
    onRemoveAddressSuccess?: () => void;
    onRemoveAddressError?: (message: string) => void;
}

export type RemoveStepOptions = 'initialStep' | 'linkedToCC' | 'removeBlocked' | 'linkedConfirmation';
export type RemoveStep = {
    key: RemoveStepOptions;
    title?: string;
    description: string;
    cancelButton: boolean;
    actionButtonText: string;
    modalActionButtonText: string;
};
const REMOVE_STEP: Record<RemoveStepOptions, RemoveStep> = {
    initialStep: {
        key: 'initialStep',
        description: 'Are you sure you want to remove this address from your account?',
        cancelButton: true,
        actionButtonText: 'Remove',
        modalActionButtonText: 'Remove Address',
    },
    linkedToCC: {
        key: 'linkedToCC',
        title: 'This address cannot be removed',
        description:
            'It is associated to one or more credit cards. Change the billing address to the card or remove the credit card and try again.',
        cancelButton: false,
        actionButtonText: 'OK',
        modalActionButtonText: 'Got it',
    },
    removeBlocked: {
        key: 'removeBlocked',
        title: 'This address cannot be removed',
        description:
            'Since you have an active subscription, a default address is required on your profile. Set another address as the default to remove this address.',
        cancelButton: false,
        actionButtonText: 'OK',
        modalActionButtonText: 'Got it',
    },
    linkedConfirmation: {
        key: 'linkedConfirmation',
        description:
            'This address is linked to an Autoship order. We’ll update it to your default shipping address. You can change it before your next shipment.',
        cancelButton: true,
        actionButtonText: 'Remove',
        modalActionButtonText: 'Remove Address',
    },
};

const getRemoveAddressErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return SERVER_ERROR_PRIMARY_TEXT;
};

function addressToUpdatePayload(addr: AccountAddressesSynced, isPrimary: boolean) {
    return {
        firstName: addr.firstName ?? '',
        lastName: addr.lastName ?? '',
        countryAbbreviation: addr.countryAbbreviation ?? getCountry(),
        stateProvinceAbbreviation: addr.stateProvinceAbbreviation,
        zipPostalCode: addr.zipPostalCode,
        ...(typeof addr.postalCodeExtension === 'string' ? { postalCodeExtension: addr.postalCodeExtension } : {}),
        city: addr.city,
        streetLine1: addr.streetLine1,
        streetLine2: addr.streetLine2 ?? '',
        phoneNumber: addr.phoneNumber,
        addressType: addr.addressType ?? 'other',
        isPrimary,
    };
}

function getNextEligiblePrimaryAddress(addresses: AccountAddressesSynced[], addressToRemove: AccountAddressesSynced) {
    const eligibleAddressList = addresses?.filter(
        a => a.addressId !== addressToRemove?.addressId && a.countryAbbreviation === getCountry()
    );
    return eligibleAddressList
        ? eligibleAddressList.toSorted(
              (addressA, addressB) =>
                  new Date(addressB.createdDate).getTime() - new Date(addressA.createdDate).getTime()
          )[0]
        : null;
}

export const useRemoveAddress = ({
    defaultAddress,
    addresses,
    order,
    addressToRemove,
    handleDrawerNavigation,
    hasSuccessHandler,
    isFraudValidationNeeded = false,
    siteSection,
    onUpdate,
    handleBack,
    sentryErrorOnRemoveFailed = SentryErrors.AutoshipPage.EditAddressFailed,
    onRemoveAddressSuccess,
    onRemoveAddressError,
}: Readonly<UseRemoveAddressProps>) => {
    const { updateProfileData, userData } = useUserSession();

    const [currentStep, setCurrentStep] = useState<RemoveStepOptions>('initialStep');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isPrimary = addressToRemove?.isPrimary;
    const isLinkedToCreditCard = userData?.payments?.some(
        p => p.addressId != null && p.addressId === addressToRemove?.addressId
    );
    const hasLinkedOrders = (addressToRemove?.linkedOrders?.length ?? 0) > 0;
    const isOGOnlyAddress = !addressToRemove?.addressId || addressToRemove.addressId === 0;
    const hasDefaultAddress = Boolean(defaultAddress?.addressId);
    const nextEligiblePrimaryAddress =
        addresses && addressToRemove ? getNextEligiblePrimaryAddress(addresses, addressToRemove) : null;
    const trackRemoveAddressError = (message: string) => {
        onRemoveAddressError?.(message);
    };

    const handleRemoveAddress = async (currentStep: RemoveStepOptions) => {
        if (currentStep === 'initialStep') {
            if (isLinkedToCreditCard) {
                if (siteSection) trackShippingAddressEvents('VIEW_CANNOT_REMOVE_MODAL', siteSection);
                trackRemoveAddressError(REMOVE_STEP.linkedToCC.description);
                setCurrentStep('linkedToCC');
            } else if (isPrimary) {
                if (hasLinkedOrders) {
                    if (siteSection) trackShippingAddressEvents('VIEW_CANNOT_REMOVE_MODAL', siteSection);
                    trackRemoveAddressError(REMOVE_STEP.removeBlocked.description);
                    setCurrentStep('removeBlocked');
                } else {
                    await removeAddressAction({ hasToUpdatePrimaryAddress: true });
                }
            } else if (isOGOnlyAddress || hasLinkedOrders) {
                if (hasDefaultAddress) {
                    setCurrentStep('linkedConfirmation');
                } else {
                    if (siteSection) trackShippingAddressEvents('VIEW_CANNOT_REMOVE_MODAL', siteSection);
                    trackRemoveAddressError(REMOVE_STEP.removeBlocked.description);
                    setCurrentStep('removeBlocked');
                }
            } else {
                await removeAddressAction({ hasToUpdatePrimaryAddress: false });
            }
        }

        if (['linkedToCC', 'removeBlocked'].includes(currentStep)) {
            handleBack();
            setCurrentStep('initialStep');
        }

        if (['linkedConfirmation'].includes(currentStep)) {
            await removeAddressAction({ hasToUpdatePrimaryAddress: false });
            setCurrentStep('initialStep');
        }
    };

    const removeAddressAction = async ({ hasToUpdatePrimaryAddress }: { hasToUpdatePrimaryAddress: boolean }) => {
        try {
            setIsLoading(true);
            if (!addressToRemove) {
                return;
            }
            let addressData;

            // Update default if neede and remove curernt address
            if (hasToUpdatePrimaryAddress && nextEligiblePrimaryAddress) {
                const payload = addressToUpdatePayload(nextEligiblePrimaryAddress, true);
                const updateResponse = await updateAddress(nextEligiblePrimaryAddress.addressId, payload);

                if (!updateResponse) {
                    throw new Error('Address API request failed');
                }

                addressData = {
                    firstName: nextEligiblePrimaryAddress.firstName ?? '',
                    lastName: nextEligiblePrimaryAddress.lastName ?? '',
                    line1: nextEligiblePrimaryAddress.streetLine1,
                    line2: nextEligiblePrimaryAddress.streetLine2,
                    city: nextEligiblePrimaryAddress.city,
                    state: nextEligiblePrimaryAddress.stateProvinceAbbreviation,
                    postalCode: nextEligiblePrimaryAddress.zipPostalCode,
                    country: getCountry(),
                    phone: nextEligiblePrimaryAddress.phoneNumber,
                    ...withPodsAddressId(nextEligiblePrimaryAddress.addressId),
                };
            } else {
                if (!hasDefaultAddress && (isOGOnlyAddress || hasLinkedOrders)) {
                    if (siteSection) {
                        trackShippingAddressEvents('VIEW_CANNOT_REMOVE_MODAL', siteSection);
                    }
                    trackRemoveAddressError(REMOVE_STEP.removeBlocked.description);
                    setCurrentStep('removeBlocked');
                    return;
                }
                addressData = {
                    firstName: defaultAddress?.firstName ?? '',
                    lastName: defaultAddress?.lastName ?? '',
                    line1: defaultAddress?.streetLine1 ?? '',
                    line2: defaultAddress?.streetLine2 ?? '',
                    city: defaultAddress?.city ?? '',
                    state: defaultAddress?.stateProvinceAbbreviation ?? '',
                    postalCode: defaultAddress?.zipPostalCode ?? '',
                    country: getCountry(),
                    phone: defaultAddress?.phoneNumber ?? '',
                    ...withPodsAddressId(defaultAddress?.addressId),
                };
            }

            // Remove Address if not OG only
            if (!isOGOnlyAddress) {
                const removeResponse = await removeAddress(addressToRemove.addressId);

                if (!(removeResponse && [200, 204].includes(removeResponse.status))) {
                    throw new Error('Failed to delete address');
                }

                await updateProfileData?.(PROFILE_DATA.ADDRESSES);
                await updateProfileData?.(PROFILE_DATA.PROFILE);
            }

            // Update OG Order/Subscription with default address
            if (!isFraudValidationNeeded) {
                let orderIds: string[] = [];
                let orderSubscriptions: string[] = [];
                if (isOGOnlyAddress && order) {
                    const currentOrderIds = [order.id];
                    const currentOrderSubscriptions = order.items.reduce<string[]>((acc, item) => {
                        if (item.subscription) {
                            acc.push(item.subscription.id);
                        }
                        return acc;
                    }, []);

                    orderIds = [...orderIds, ...currentOrderIds];
                    orderSubscriptions = [...orderSubscriptions, ...currentOrderSubscriptions];
                } else if (addressToRemove.linkedOrders) {
                    const addressOrders = addressToRemove.linkedOrders.map(order => order.orderId);
                    const addressOrderSubscriptions = addressToRemove.linkedOrders.reduce<string[]>(
                        (subscriptionsIds, order) => {
                            if (order.subscriptionsIds) {
                                subscriptionsIds.push(...order.subscriptionsIds);
                            }
                            return subscriptionsIds;
                        },
                        []
                    );

                    orderIds = [...orderIds, ...addressOrders];
                    orderSubscriptions = [...orderSubscriptions, ...addressOrderSubscriptions];
                }
                if (orderIds.length && orderSubscriptions.length) {
                    await updateShippingAddress(orderIds, orderSubscriptions, addressData);
                }
            }

            await onUpdate({ ignoreLoadingState: true });
            if (!hasSuccessHandler) {
                SnackbarUtils.show({ primaryText: 'Your preferences have been saved' });
            }
            onRemoveAddressSuccess?.();
            if (siteSection) trackShippingAddressEvents('REMOVE', siteSection);
            handleDrawerNavigation.navigate('AddressList', true);
        } catch (err) {
            logSentryError(err, sentryErrorOnRemoveFailed);
            SnackbarUtils.showPredefined('SERVER_ERROR');
            onRemoveAddressError?.(getRemoveAddressErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    return {
        currentStep,
        isLoading,
        REMOVE_STEP,
        handleRemoveAddress,
        removeAddressAction,
        setCurrentStep,
    };
};
